import type { EnrollmentStepSubmissionData } from '@/api/enrollment-api'
import type { EligibilityFormData } from '../../../../components/eligibilitySteps/types'
import { isMaskedStoredSsn } from '../../../../components/eligibilitySteps/contactDetails/helpers'
import { normalizeStateCode } from '../../../../components/eligibilitySteps/states'
import { formatDateForApi, formatTransmissionForApi } from '../../utils/utils'

export function buildAdditionalInfoStepData(
  formData: EligibilityFormData,
  shouldShowLaptopQuestion: boolean
): Pick<EnrollmentStepSubmissionData, 'trainingMode' | 'hasLaptop'> {
  return {
    trainingMode: shouldShowLaptopQuestion ? formData.trainingMode || undefined : undefined,
    hasLaptop: shouldShowLaptopQuestion ? formData.hasLaptop : undefined
  }
}

export function buildEligibilityStepData(
  formData: EligibilityFormData,
  usCitizen: boolean
): Pick<
  EnrollmentStepSubmissionData,
  'dateOfBirth' | 'usCitizen' | 'gender' | 'ethnicity' | 'educationLevel'
> {
  return {
    dateOfBirth: formatDateForApi(formData.dateOfBirth),
    usCitizen,
    gender: formData.gender || undefined,
    ethnicity: formData.ethnicity || undefined,
    educationLevel: formData.educationLevel || undefined
  }
}

export function buildLicenseDetailsStepData(
  formData: EligibilityFormData
): Pick<
  EnrollmentStepSubmissionData,
  | 'driversLicenseState'
  | 'driversLicenseNumber'
  | 'driversLicenseIssueDate'
  | 'driversLicenseExpiry'
> {
  return {
    driversLicenseState: normalizeStateCode(formData.driverLicenseState) || undefined,
    driversLicenseNumber: formData.driverLicenseNumber || undefined,
    driversLicenseIssueDate: formatDateForApi(formData.licenseIssueDate),
    driversLicenseExpiry: formatDateForApi(formData.licenseExpirationDate)
  }
}

export function getContactDetailsValidationError(
  formData: EligibilityFormData,
  invalidEmergencyPhoneMessage: string
) {
  const ssnDigits = formData.ssn.replace(/\D/g, '')
  const emergencyContactPhoneDigits = formData.emergencyContactPhone.replace(/\D/g, '')
  const hasMaskedPrefilledSsn = isMaskedStoredSsn(formData.ssn)

  if (!hasMaskedPrefilledSsn && ssnDigits.length !== 9) {
    return 'Please enter a valid 9-digit Social Security Number.'
  }

  if (emergencyContactPhoneDigits.length === 0) {
    return 'Please enter an emergency contact phone number.'
  }

  if (emergencyContactPhoneDigits.length !== 10) {
    return invalidEmergencyPhoneMessage
  }

  return null
}

export function buildContactDetailsStepData(
  formData: EligibilityFormData
): Pick<
  EnrollmentStepSubmissionData,
  'ssnEncrypted' | 'ssnLastFour' | 'emergencyContactName' | 'emergencyContactPhone'
> {
  const ssnDigits = formData.ssn.replace(/\D/g, '')
  const hasMaskedPrefilledSsn = isMaskedStoredSsn(formData.ssn)

  return {
    ssnEncrypted: hasMaskedPrefilledSsn ? undefined : ssnDigits || undefined,
    ssnLastFour: hasMaskedPrefilledSsn ? undefined : ssnDigits.slice(-4) || undefined,
    emergencyContactName: formData.emergencyContactName.trim() || undefined,
    emergencyContactPhone: formData.emergencyContactPhone
  }
}

export function buildLocationSelectionStepData(
  formData: EligibilityFormData,
  locationValue: string
): Pick<EnrollmentStepSubmissionData, 'zipCode' | 'locationExternalId' | 'preferredTransmission'> {
  return {
    zipCode: formData.zipCode,
    locationExternalId: locationValue,
    preferredTransmission:
      formatTransmissionForApi(formData.transmissionType) as EnrollmentStepSubmissionData['preferredTransmission']
  }
}

export function buildTrainingDateStepData(
  formData: EligibilityFormData
): Pick<EnrollmentStepSubmissionData, 'preferredStartDate' | 'yardDays'> {
  return {
    preferredStartDate: formData.trainingStartDate || undefined,
    yardDays: formData.yardDays
  }
}
